/* eslint-disable no-use-before-define */
import { cloneDeep, pick } from 'lodash-es'
import { mergeTracks } from '@com/components/timeline-panel/utils/track-merge'
import { resolveSegmentFramesIfGap } from '@com/components/timeline-panel/utils/segment'
import { IvAssetType, IvSegmentType, IvTrackType } from '../enums'
import { getIvAsset, getTimelineBySceneUuid, setTimeline } from '../timeline'
import {
  IvComponentSegment,
  IvNodeUuid,
  IvSegment,
  IvSegmentFreeze,
  IvSegmentLoop,
  IvSegmentVideo,
  IvTrack,
  TypeToSegment,
} from '../types'
import {
  addIvSegment,
  addIvSegmentToTrack,
  createIvTrack,
  patchTracks,
  removeIvSegment,
} from './track-action'
import {
  resolveIvSegment,
  resolveIvSegmentFreeze,
  resolveIvSegmentHand,
  resolveIvSegmentHold,
  resolveIvSegmentLoop,
  resolveIvSegmentText,
  resolveIvSegmentVideo,
} from './resolve-segment'
import {
  getIvRelativedSegments,
  getSceneSegments,
  isIvSegment,
  isSegmentIv,
  isSegmentVideo,
} from '.'
import {
  copySegment,
  CopySegmentOptions,
  patchSegment,
  removeSegmentIv,
  updateByVideoSegment,
  updateSegment,
  updateSegmentIvType,
} from './segment'
import nodePool from '@ps/bridge/qc/node-pool'
import { getSceneNodeByUuid, isEmptyNode, isText } from '@ps/bridge/qc/base'
import { findIvSegmentInsertableStart } from '@ps/views/interactive-video/timeline/utiles/segment-drag'
import { IvBrainItem } from '../../brain/types'
import { IvLinkType } from '../../brain/enums'
import { TrackFrameUnit } from '@com/components/timeline-panel/types'
import store from '@ps/store'
import * as sceneConfigScript from '@ps/bridge/qc/scripts/scene-config'
import { getSceneHotSpotGroup } from '../../brain/brain'
import {
  createSceneError,
  InferSceneErrorType,
  isSceneError,
  SceneError,
  SceneErrorType,
} from './scene-error'

export function findVideoSegmentFromTracks(
  sceneUuid: IvSegment['sceneUuid'],
  tracks: IvTrack[],
): IvSegmentVideo | null {
  const targetVideoTrack = tracks.find((t) =>
    t.segments?.filter(isIvSegment)?.some((s) => isSegmentVideo(s)),
  )

  const targetVideoSegment = targetVideoTrack?.segments
    ?.filter(isIvSegment)
    .filter(isSegmentVideo)
    .find((s) => isSegmentVideo(s) && s.sceneUuid === sceneUuid)

  return targetVideoSegment ?? null
}

/**
 * 判断指定数据集是否是一个空场景
 */
export function isEmptyScene(
  sceneUuid: IvNodeUuid,
  data: IvTrack[] | null | undefined,
): boolean {
  const videoSegment = findVideoSegmentFromTracks(sceneUuid, data || [])
  if (!data || data.length === 0 || !videoSegment) {
    return true
  }

  return false
}

/**
 * 创建一个空场景剧本
 */
export function createEmptySceneTracks(
  sceneUuid: IvNodeUuid,
  segment?: IvSegmentVideo,
): IvTrack[] {
  const t = createIvTrack(IvTrackType.Video)

  segment = segment ?? resolveIvSegmentVideo()
  segment.sceneUuid = sceneUuid

  addIvSegment(segment, t)
  return [t]
}

/**
 * 根据节点id创建元件
 * @param nodeUuid
 * @returns
 */
// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export function resolveSceneSegment(nodeUuid: IvNodeUuid) {
  const node = nodePool.find(nodeUuid)
  if (!node) {
    return null
  }

  if (isText(node)) {
    const textSegment = resolveIvSegmentText()
    textSegment.text = node.text || ''
    return textSegment
  }

  if (isEmptyNode(node)) {
    // 手势区域
    return resolveIvSegmentHand()
  }

  const asset = getIvAsset(nodeUuid)

  if (!asset) {
    return null
  }

  return resolveIvSegment(asset)
}

function findEndSegmentIvFromTracks(
  data: IvTrack[],
): IvComponentSegment | null {
  for (let index = 0; index < data.length; index++) {
    const track = data[index]
    if (track?.type === IvTrackType.Iv) {
      const target = track?.segments.find((s) => s.isEnd)
      if (target) return target
    }
  }

  return null
}

interface SegmentVideoToSceneOptions {
  link?: IvBrainItem['link']
}
// eslint-disable-next-line @typescript-eslint/no-empty-interface
interface AddSegmentVideoToSceneOptions extends SegmentVideoToSceneOptions {}
// eslint-disable-next-line @typescript-eslint/no-empty-interface
interface UpdateSegmentVideoToSceneOptions extends SegmentVideoToSceneOptions {}

/**
 * 添加视频元件
 */
export function addSegmentVideoToScene(
  nodeUuid: IvNodeUuid,
  sceneUuid: IvSegment['sceneUuid'],
  options?: AddSegmentVideoToSceneOptions,
): IvSegmentVideo | null {
  const { link } = options ?? {}
  const asset = getIvAsset(nodeUuid)
  if (asset?.type === IvAssetType.Video) {
    const segment = resolveIvSegmentVideo(asset)
    segment.id = nodeUuid
    segment.sceneUuid = sceneUuid
    const tracks = createEmptySceneTracks(sceneUuid, segment)

    setTimeline(sceneUuid, tracks)

    if (link) {
      addEndSegmentToScene(sceneUuid, link)
    }

    return segment
  }

  return null
}

export function updateSegmentVideoToScene(
  nodeUuid: IvNodeUuid,
  sceneUuid: IvSegment['sceneUuid'],
  options?: UpdateSegmentVideoToSceneOptions,
): IvSegmentVideo | null {
  const tracks = getTimelineBySceneUuid(sceneUuid)
  const videoSegment = findVideoSegmentFromTracks(sceneUuid, tracks)
  const videoTrack = tracks.find((t) => t.type === IvTrackType.Video)
  if (!videoSegment || !videoTrack) {
    return addSegmentVideoToScene(nodeUuid, sceneUuid, options)
  }

  const asset = getIvAsset(nodeUuid)
  if (asset?.type === IvAssetType.Video) {
    const newVideosegment = resolveIvSegmentVideo(asset)
    newVideosegment.id = nodeUuid
    newVideosegment.sceneUuid = sceneUuid

    removeIvSegment(videoSegment, tracks)
    addIvSegment(newVideosegment, videoTrack)
    // 重置视频所在父节点
    newVideosegment.groupId = nodeUuid

    // 压缩场景内容的元件
    updateByVideoSegment(sceneUuid, tracks)

    setTimeline(sceneUuid, tracks)
    return newVideosegment
  }

  return null
}

export function addEndSegmentToScene(
  sceneUuid: IvBrainItem['sceneUuid'],
  link: IvBrainItem['link'],
):
  | ReturnType<typeof addEndSegmentFreezeToScene>
  | ReturnType<typeof addEndSegmentLoopToScene> {
  if (link.type === IvLinkType.Interactive && link.interactiveType) {
    if (link.interactiveType === IvSegmentType.Freeze) {
      return addEndSegmentFreezeToScene(link.id, sceneUuid)
    } else if (link.interactiveType === IvSegmentType.Loop) {
      return addEndSegmentLoopToScene(link.id, sceneUuid)
    }
  }

  return createSceneError(SceneErrorType.InsertFail)
}

/**
 * 添加场景间互动定格元件
 */
export function addEndSegmentFreezeToScene(
  nodeUuid: IvNodeUuid,
  sceneUuid: IvSegment['sceneUuid'],
):
  | IvSegmentFreeze
  | SceneError<
      | SceneErrorType.NoSpace
      | SceneErrorType.NoVideo
      | SceneErrorType.InsertFail
    > {
  const tracks = getTimelineBySceneUuid(sceneUuid)
  const videoSegment = findVideoSegmentFromTracks(sceneUuid, tracks)
  if (!videoSegment) return createSceneError(SceneErrorType.NoVideo)

  const segment = resolveIvSegmentFreeze()
  segment.id = nodeUuid
  segment.sceneUuid = sceneUuid
  const space = findEndSegmentIvSpace(sceneUuid, tracks)
  if (isSceneError(space)) {
    return space
  }
  segment.start = videoSegment.frames - 1
  const result = addSceneEndSegment(segment, tracks)
  if (result) {
    patchEndSegmentGroupId(result)
  }

  setTimeline(sceneUuid, tracks)

  return result || createSceneError(SceneErrorType.InsertFail)
}

/**
 * 添加场景间互动循环元件
 */
export function addEndSegmentLoopToScene(
  nodeUuid: IvNodeUuid,
  sceneUuid: IvSegment['sceneUuid'],
):
  | IvSegmentLoop
  | SceneError<
      | SceneErrorType.NoSpace
      | SceneErrorType.NoVideo
      | SceneErrorType.InsertFail
    > {
  const tracks = getTimelineBySceneUuid(sceneUuid)
  const videoSegment = findVideoSegmentFromTracks(sceneUuid, tracks)
  if (!videoSegment) return createSceneError(SceneErrorType.NoVideo)

  const segment = resolveIvSegmentLoop()
  segment.id = nodeUuid
  segment.sceneUuid = sceneUuid

  const space = findEndSegmentIvSpace(sceneUuid, tracks)
  if (isSceneError(space)) {
    return space
  }

  segment.frames = Math.min(segment.frames, space.frames)
  segment.start = videoSegment.frames - segment.frames
  const result = addSceneEndSegment(segment, tracks)
  if (result) {
    patchEndSegmentGroupId(result)
  }

  setTimeline(sceneUuid, tracks)

  return result || createSceneError(SceneErrorType.InsertFail)
}

function findEndSegmentIvSpace(
  sceneUuid: IvSegment['sceneUuid'],
  tracks: IvTrack[],
):
  | Pick<IvComponentSegment, 'start' | 'frames'>
  | SceneError<SceneErrorType.NoVideo | SceneErrorType.NoSpace> {
  const videoSegment = findVideoSegmentFromTracks(sceneUuid, tracks)
  if (!videoSegment) return createSceneError(SceneErrorType.NoVideo)
  const targetIvTrack = tracks.find((t) => t.type === IvTrackType.Iv)

  const segments = [...(targetIvTrack?.segments ?? [])].sort(
    (a, b) => a.start - b.start,
  )

  const last = segments[segments.length - 1]

  const start = last ? last.start + resolveSegmentFramesIfGap(last) : 0
  const frames = videoSegment.frames - start

  if (frames === 0) return createSceneError(SceneErrorType.NoSpace)

  return { start, frames }
}
function patchEndSegmentGroupId(target: IvSegmentFreeze | IvSegmentLoop) {
  const currentSceneNode = nodePool.find(target.sceneUuid)
  const spotGroup = currentSceneNode && getSceneHotSpotGroup(currentSceneNode)
  target.groupId = spotGroup?.uuid ?? ''
}

/**
 * 添加场景间的互动元件
 * 支持一下元件
 * 1. 互动定格
 * 2. 互动循环
 */
function addSceneEndSegment<T extends IvSegmentFreeze | IvSegmentLoop>(
  segment: T,
  tracks: IvTrack[],
): T | null {
  const endSegment = findEndSegmentIvFromTracks(tracks)

  // 存在表示添加失败
  if (endSegment) {
    return null
  }
  segment.isEnd = true

  patchSegment(segment, tracks)

  return addIvSegmentToTrack(segment, tracks) ? segment : null
}
function resolveSegmentIvByType<T extends IvComponentSegment['type']>(
  type: T,
): TypeToSegment<T, IvComponentSegment> {
  const segmentTypeMaps = {
    [IvSegmentType.Freeze]: resolveIvSegmentFreeze,
    [IvSegmentType.Hold]: resolveIvSegmentHold,
    [IvSegmentType.Loop]: resolveIvSegmentLoop,
  }

  return segmentTypeMaps[type]() as any
}

/**
 * 添加场景内的互动元件
 */
export function addSegmentIvToSceneByStore<
  T extends IvComponentSegment['type'],
>(options: {
  nodeUuid: IvNodeUuid
  sceneUuid: IvSegment['sceneUuid']
  type: T
}): ReturnType<typeof addSegmentIvToScene<T>> {
  const { sceneUuid } = options
  // 当前光标
  const currentFrame = store.state.ivTimeline.currentFrame

  const mergedTracks: IvTrack[] = store.state.ivTimeline.timelineData || []

  const videoTrack = mergedTracks.find((t) => t.type === IvTrackType.Video)
  const targetSceneVideosegment = videoTrack?.segments.find(
    (s) => s.sceneUuid === sceneUuid,
  )

  if (targetSceneVideosegment) {
    const start = currentFrame - targetSceneVideosegment.start
    return addSegmentIvToScene({
      ...options,
      start,
    })
  }

  return createSceneError(SceneErrorType.NoVideo)
}
export function addSegmentIvToScene<
  T extends IvComponentSegment['type'],
>(options: {
  nodeUuid: IvNodeUuid
  sceneUuid: IvSegment['sceneUuid']
  start: IvSegment['start']
  type: T
}): ReturnType<
  typeof addSegmentToTracks<TypeToSegment<T, IvComponentSegment>>
> {
  const { type, nodeUuid, sceneUuid, start } = options

  const tracks = getTimelineBySceneUuid(sceneUuid)
  const segment = resolveSegmentIvByType(type)
  segment.id = nodeUuid
  segment.sceneUuid = sceneUuid
  segment.start = start

  const r = addSegmentToTracks(segment, tracks)

  setTimeline(sceneUuid, tracks)

  return r
}

export function addSegmentOtherToSceneByStore(
  nodeUuid: IvNodeUuid,
  sceneUuid: IvSegment['sceneUuid'],
): ReturnType<typeof addSegmentOtherToScene> {
  const timelineTracks: IvTrack[] = store.state.ivTimeline.timelineData || []
  // 当前光标
  const currentFrame = store.state.ivTimeline.currentFrame
  const videoSegment = findVideoSegmentFromTracks(sceneUuid, timelineTracks)

  // 优先选中的互动的元件，其次是光标所在互动元件
  const ivTrack = timelineTracks.find((t) => t.type === IvTrackType.Iv)
  const trackSegments =
    ivTrack?.segments.filter((s) => s.sceneUuid === sceneUuid) ?? []

  const riv =
    trackSegments.find((s) => s.selected) ??
    trackSegments.find(
      (s) =>
        currentFrame >= s.start &&
        currentFrame < s.start + resolveSegmentFramesIfGap(s),
    )

  if (videoSegment) {
    if (riv) {
      return addSegmentOtherToScene({
        nodeUuid,
        sceneUuid,
        riv: { ...riv, start: riv.start - videoSegment.start },
      })
    }

    return addSegmentOtherToScene({
      nodeUuid,
      sceneUuid,
      start: currentFrame - videoSegment.start,
    })
  }

  return createSceneError(SceneErrorType.NoVideo)
}

interface AddSceneSegmentByStart {
  nodeUuid: IvNodeUuid
  sceneUuid: IvSegment['sceneUuid']
  start: IvSegment['start']
}
interface AddSceneSegmentByRelativedIv {
  nodeUuid: IvNodeUuid
  sceneUuid: IvSegment['sceneUuid']
  riv: IvComponentSegment
}

/**
 * 添加场景内的非互动元件(包含作为附属元件)
 */
export function addSegmentOtherToScene(
  options: AddSceneSegmentByRelativedIv | AddSceneSegmentByStart,
):
  | ReturnType<
      typeof addSegmentToTracks<
        NonNullable<ReturnType<typeof resolveSceneSegment>>
      >
    >
  | SceneError<SceneErrorType.SegmentNotFound> {
  const { nodeUuid, sceneUuid } = options

  const tracks = getTimelineBySceneUuid(sceneUuid)
  const segment = resolveSceneSegment(nodeUuid)
  if (!segment) return createSceneError(SceneErrorType.SegmentNotFound)
  segment.id = nodeUuid
  segment.sceneUuid = sceneUuid

  const isRiv = (o: Record<string, any>): o is AddSceneSegmentByRelativedIv => {
    // eslint-disable-next-line no-prototype-builtins
    return o.hasOwnProperty('riv')
  }
  if (isRiv(options)) {
    segment.start = options.riv.start
    segment.frames = resolveSegmentFramesIfGap(options.riv)
  } else {
    segment.start = options.start
  }

  const r = addSegmentToTracks(segment, tracks)

  setTimeline(sceneUuid, tracks)

  return r
}

/**
 * 添加场景内的元件
 */
function addSegmentToTracks<T extends IvSegment>(
  segment: T,
  tracks: IvTrack[],
):
  | T
  | SceneError<
      | SceneErrorType.NoVideo
      | SceneErrorType.NoSpace
      | SceneErrorType.InsertFail
    > {
  const videoSegment = findVideoSegmentFromTracks(segment.sceneUuid, tracks)

  if (!videoSegment) {
    return createSceneError(SceneErrorType.NoVideo)
  }

  // 保证进场在视频范围内
  segment.start = Math.min(
    Math.max(segment.start, videoSegment.start),
    videoSegment.start + videoSegment.frames - 1,
  )

  if (isSegmentIv(segment)) {
    // 预测：默认范围内的默认时长，是否和现有的元件冲突了
    const v = findIvSegmentInsertableStart({ ...segment, frames: 1 }, tracks)
    // 没位置放了
    if (v === -1) {
      return createSceneError(SceneErrorType.NoSpace)
    }

    const sceneSegments = getSceneSegments(segment.sceneUuid, tracks)
    const sceneIvSegments = sceneSegments.filter(isSegmentIv)
    const sortedSceneIvSegments = sceneIvSegments.sort(
      (a, b) => a.start - b.start,
    )
    segment.isEnd = false

    // 找到的位置，是在指定位置的左侧
    if (v < segment.start) {
      // 可插入位置跟指定位置不一致
      const vAhead = [...sortedSceneIvSegments]
        .reverse()
        .find((s) => v >= s.start + resolveSegmentFramesIfGap(s))
      const vBehind = sortedSceneIvSegments.find((s) => s.start >= v)

      const farLeftStart = vAhead
        ? vAhead.start + resolveSegmentFramesIfGap(vAhead)
        : videoSegment.start

      const farRightEnd = vBehind
        ? vBehind.start
        : videoSegment.start + videoSegment.frames

      segment.start = Math.max(
        farLeftStart,
        farRightEnd - resolveSegmentFramesIfGap(segment),
      )
    } else {
      segment.start = v
    }

    const behind = sortedSceneIvSegments.find((s) => s.start >= segment.start)

    if (segment.is !== 'gap' && behind) {
      segment.frames = Math.min(segment.frames, behind.start - segment.start)
    }
  }
  if (segment.is !== 'gap') {
    segment.frames = Math.min(
      segment.frames,
      videoSegment.start + videoSegment.frames - segment.start,
    )
  }

  if (segment.frames === 0) {
    return createSceneError(SceneErrorType.NoSpace)
  }

  patchSegment(segment, tracks)

  return addIvSegmentToTrack(segment, tracks)
    ? segment
    : createSceneError(SceneErrorType.InsertFail)
}

/**
 * 删除场景的互动元件
 */
export function removeSegmentIvToScene(
  sceneUuid: IvSegment['sceneUuid'],
  nodeUuid: IvNodeUuid,
): IvComponentSegment | null {
  const tracks = getTimelineBySceneUuid(sceneUuid)
  const sceneSegments = getSceneSegments(sceneUuid, tracks)
  const target = sceneSegments.find((s) => s.id === nodeUuid)

  if (target && isSegmentIv(target)) {
    removeSegmentIv(target, tracks)
    setTimeline(sceneUuid, tracks)
    return target
  }

  return null
}

/**
 * 单独删除指定元件（所有元件类型）
 */
export function removeIvSegmentToScene(
  sceneUuid: IvSegment['sceneUuid'],
  nodeUuid: IvNodeUuid,
): IvSegment | null {
  const tracks = getTimelineBySceneUuid(sceneUuid)
  const sceneSegments = getSceneSegments(sceneUuid, tracks)
  const target = sceneSegments.find((s) => s.id === nodeUuid)

  if (target) {
    removeIvSegment(target, tracks, {
      removeTrackIfEmpty: true,
    })
    setTimeline(sceneUuid, tracks)
    return target
  }

  return null
}

export function updateSegmentIvTypeToScene(options: {
  nodeUuid: IvNodeUuid
  sceneUuid: IvSegment['sceneUuid']
  type: IvComponentSegment['type']
}): void {
  const { type, sceneUuid, nodeUuid } = options
  const tracks = getTimelineBySceneUuid(sceneUuid)
  const sceneSegments = getSceneSegments(sceneUuid, tracks)
  const target = sceneSegments.find((s) => s.id === nodeUuid)

  if (target && isSegmentIv(target)) {
    updateSegmentIvType(target, type, tracks)
    setTimeline(sceneUuid, tracks)
  }
}

type NodeUuidMap = Record<IvNodeUuid, IvNodeUuid>
export interface CopyPasteSegmentOptions<T extends CopypasteSegment>
  extends Pick<CopySegmentOptions<T>, 'override'> {
  from: Pick<T, 'id' | 'sceneUuid'>
  resolveNodeUuidMap: (sourceNodeUuids: IvNodeUuid[]) => NodeUuidMap
}

interface CopyPasteSegmentResult<T extends CopypasteSegment> {
  target: T
  relatived: ReturnType<typeof getIvRelativedSegments>
}
export type CopypasteSegment = Exclude<IvSegment, IvSegmentVideo>

/**
 * 将指定元件粘贴到指定位置
 * 1.节点内的互动元件支持复制，附属元件一并复制, 由于只有一条互动轨道，如果当前节点互动轨道填满，粘贴异常提示可以和添加互动元件保持一致
 * 2.支持跨场景复制粘贴
 * 3.视频不能复制粘贴
 */
export function copypasteSegmentToScene<T extends CopypasteSegment>(
  options: CopyPasteSegmentOptions<T>,
):
  | CopyPasteSegmentResult<T>
  | SceneError<
      | SceneErrorType.NoVideo
      | SceneErrorType.SegmentNotFound
      | SceneErrorType.CreateNodeFail
    >
  | InferSceneErrorType<ReturnType<typeof addSegmentToTracks<T>>> {
  const { from, override, resolveNodeUuidMap } = options

  const oldTarget = findSceneSegment<T>(from.sceneUuid, from.id)
  if (!oldTarget) {
    return createSceneError(SceneErrorType.SegmentNotFound)
  }

  const relativedSegments = isSegmentIv(oldTarget)
    ? getIvRelativedSegments(
        oldTarget,
        getTimelineBySceneUuid(oldTarget.sceneUuid),
      )
    : []

  const newTargetTracks = getTimelineBySceneUuid(override.sceneUuid)

  const newTarget = addSegmentToTracks(
    copySegment({
      target: oldTarget,
      override,
    }),
    newTargetTracks,
  )

  if (isSceneError(newTarget)) {
    return newTarget
  }

  const sourceNodeUuids = [oldTarget.id, ...relativedSegments.map((s) => s.id)]
  const nodeUuidMap = resolveNodeUuidMap(sourceNodeUuids)

  const newTargetId = nodeUuidMap[oldTarget.id]
  if (!newTargetId || sourceNodeUuids.some((s) => !nodeUuidMap[s])) {
    return createSceneError(SceneErrorType.CreateNodeFail)
  }
  const syncSegment = (
    target: T | typeof relativedSegments[number],
    values: Pick<T, 'id' | 'frames'>,
  ) => {
    Object.assign(target, values)
  }

  syncSegment(newTarget, {
    id: newTargetId,
    frames: newTarget.frames,
  })
  type IvRelativedSegmentType = ReturnType<
    typeof getIvRelativedSegments
  >[number]
  const oldRelativedSegmentsTracks = getTimelineBySceneUuid(oldTarget.sceneUuid)
    .filter((t) => relativedSegments.some((s) => s.trackId === t.id))
    .map<IvTrack>((t) => {
      return {
        ...t,
        segments: t.segments
          .filter((s): s is IvRelativedSegmentType =>
            relativedSegments.some(
              (relativedSegment) => s.id === relativedSegment.id,
            ),
          )
          .map<IvRelativedSegmentType>((oldRelativedSegment) => {
            const newRelativedSegment = copySegment({
              target: oldRelativedSegment,
              override: {
                start: newTarget.start,
                sceneUuid: newTarget.sceneUuid,
              },
            })
            const newRelativedSegmentId = nodeUuidMap[oldRelativedSegment.id]!

            syncSegment(newRelativedSegment, {
              id: newRelativedSegmentId,
              frames: resolveSegmentFramesIfGap(newTarget),
            })
            newRelativedSegment.groupId = ''

            return newRelativedSegment
          }),
      } as any
    })

  const data = mergeTracks(
    newTargetTracks,
    [...oldRelativedSegmentsTracks, createIvTrack(IvTrackType.Video)],
    {
      isMainTrack(track) {
        return track.type === IvTrackType.Video
      },
      isPinnedTrack(track) {
        return track.type === IvTrackType.Iv
      },
      addSegment(segment, track) {
        addIvSegment(segment as any, track as any)
      },
    },
  )

  setTimeline(override.sceneUuid, data)

  const result: CopyPasteSegmentResult<T> = {
    target: newTarget,
    relatived: isSegmentIv(newTarget)
      ? getIvRelativedSegments(
          newTarget,
          getTimelineBySceneUuid(newTarget.sceneUuid),
        )
      : [],
  }

  return result
}

export function copypasteSegmentToSceneByStore<
  T extends CopypasteSegment = CopypasteSegment,
>(
  nodeUuid: IvSegment['id'],
  options: Pick<CopyPasteSegmentOptions<T>, 'resolveNodeUuidMap'>,
):
  | ReturnType<typeof copypasteSegmentToScene<T>>
  | SceneError<SceneErrorType.NoVideo> {
  const sceneUuid = getSceneNodeByUuid(nodeUuid)?.uuid
  // 当前光标
  const currentFrame = store.state.ivTimeline.currentFrame
  const mergedTracks: IvTrack[] = store.state.ivTimeline.timelineData || []
  const from = sceneUuid ? findSceneSegment(sceneUuid, nodeUuid) : null

  if (!from) {
    return createSceneError(SceneErrorType.SegmentNotFound)
  }

  const targetSceneUuid = findCurrentScene(currentFrame, mergedTracks)
  const targetSceneVideosegment = targetSceneUuid
    ? findVideoSegmentFromTracks(targetSceneUuid, mergedTracks)
    : null

  if (!targetSceneUuid || !targetSceneVideosegment) {
    return createSceneError(SceneErrorType.NoVideo)
  }

  return copypasteSegmentToScene<T>({
    from,
    override: {
      sceneUuid: targetSceneUuid,
      start: currentFrame - targetSceneVideosegment.start,
    },
    ...pick(options, 'resolveNodeUuidMap'),
  })
}

export function mergeSceneTracks(
  sceneUuids: IvSegment['sceneUuid'][],
): IvTrack[] {
  const [target, ...sources] = sceneUuids.map((sceneUuid) => {
    const sceneNode = nodePool.find(sceneUuid)
    const config = sceneNode
      ? sceneConfigScript.getScript(sceneNode)
      : undefined

    // 结束页面
    if (config?.isEnding) {
      const segment = resolveIvSegmentVideo()
      segment.isEnd = true
      return createEmptySceneTracks(sceneUuid, segment)
    }

    const data = getTimelineBySceneUuid(sceneUuid) || []
    if (isEmptyScene(sceneUuid, data)) {
      return createEmptySceneTracks(sceneUuid)
    }

    data.forEach((t) => {
      t.segments.forEach((s) => {
        if (s.type === IvSegmentType.Text) {
          const node = nodePool.find(s.id)
          if (node) {
            s.text = node.text || ''
          }
        }
      })
    })
    return data
  })

  if (!target) {
    return []
  }

  const tracks = sources.reduce((base, _source) => {
    const source = cloneDeep(_source)
    const videoTrack = base.find((t) => t.type === IvTrackType.Video)
    const sortedVideoSegments = videoTrack?.segments.sort(
      (a, b) => a.start - b.start,
    )
    const lastVideoSegment =
      sortedVideoSegments?.[sortedVideoSegments.length - 1]

    const start =
      (lastVideoSegment?.start ?? 0) + (lastVideoSegment?.frames ?? 0)

    source.forEach((t) => {
      t.segments.forEach((s) => {
        s.start += start
      })
    })

    return mergeTracks(base, source, {
      isMainTrack(track) {
        return track.type === IvTrackType.Video
      },
      isPinnedTrack(track) {
        return track.type === IvTrackType.Iv
      },
    })
  }, cloneDeep(target))

  patchTracks(tracks)

  return tracks
}

export function findCurrentScene(
  currentFrame: TrackFrameUnit,
  mergedTracks: IvTrack[],
): IvSegment['sceneUuid'] | undefined {
  const videoTrack = mergedTracks.find((t) => t.type === IvTrackType.Video)

  const videoSegments = videoTrack?.segments ?? []
  if (videoSegments.length === 0) {
    return undefined
  }

  const scopeSegment = videoSegments.find(
    (s) => currentFrame >= s.start && currentFrame < s.start + s.frames,
  )

  if (scopeSegment) return scopeSegment.sceneUuid

  const last = videoSegments[videoSegments.length - 1]
  return last && currentFrame === last.start + last.frames
    ? last.sceneUuid
    : undefined
}

export function findSceneSegment<T extends IvSegment = IvSegment>(
  sceneUuid: IvSegment['sceneUuid'],
  nodeUuid: IvNodeUuid,
  options?: {
    fromTracks?: IvTrack[]
  },
): T | null {
  const tracks = options?.fromTracks ?? getTimelineBySceneUuid(sceneUuid)
  const sceneSegments = getSceneSegments(sceneUuid, tracks)
  const target = sceneSegments.find<T>((s): s is T => s.id === nodeUuid)

  return target ?? null
}

export function updateSegmentToScene<T extends IvSegment = IvSegment>(
  sceneUuid: IvSegment['sceneUuid'],
  nodeUuid: IvNodeUuid,
  values: Partial<T>,
): T | null {
  const tracks = getTimelineBySceneUuid(sceneUuid)
  const target = findSceneSegment<T>(sceneUuid, nodeUuid, {
    fromTracks: tracks,
  })

  if (!target) return null

  updateSegment(target, values, tracks)
  setTimeline(sceneUuid, tracks)

  return target
}
